import { z } from 'zod'
import { normalizeBarcode } from '@sp/shared'

/**
 * Cteni carovych kodu kamerou. Chrome na Androidu ma BarcodeDetector vestaveny;
 * Safari a Firefox ne — pro ne se az pri prvnim skenovani nacte zxing-wasm.
 * Knihovna i .wasm jsou soucasti appky, nic se nestahuje odjinud.
 */

const FORMATS = ['ean_13', 'ean_8', 'upc_a']

interface Detector {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>
}

interface NativeDetectorClass {
  new (options: { formats: string[] }): Detector
  getSupportedFormats(): Promise<string[]>
}

async function nativeDetector(): Promise<Detector | null> {
  const Native = (globalThis as { BarcodeDetector?: NativeDetectorClass }).BarcodeDetector
  if (!Native) return null
  try {
    const supported = await Native.getSupportedFormats()
    if (!supported.includes('ean_13')) return null
    return new Native({ formats: FORMATS.filter((f) => supported.includes(f)) })
  } catch {
    return null
  }
}

let zxing: Promise<Detector> | null = null

function zxingDetector(): Promise<Detector> {
  zxing ??= (async () => {
    const [{ BarcodeDetector, prepareZXingModule, purgeZXingModule }, { default: wasmUrl }] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ])
    try {
      // .wasm nacist hned: chyba se tak ukaze tady, ne az tichym selhanim kazdeho snimku.
      await prepareZXingModule({
        overrides: {
          locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path),
        },
        fireImmediately: true,
      })
    } catch (err) {
      // Knihovna si nepovedene nacteni pamatuje; bez vycisteni by to uz nikdy nezkusila znovu.
      purgeZXingModule()
      throw err
    }
    return new BarcodeDetector({ formats: ['ean_13', 'ean_8', 'upc_a'] })
  })()
  // Nepovedene nacteni (treba bez signalu) at se da priste zkusit znovu.
  zxing.catch(() => {
    zxing = null
  })
  return zxing
}

export interface CodeReader {
  /** Kody zbozi v aktualnim snimku videa, uz v ulozenem tvaru. */
  read(video: HTMLVideoElement): Promise<string[]>
}

export async function createCodeReader(): Promise<CodeReader> {
  const detector = (await nativeDetector()) ?? (await zxingDetector())
  return {
    async read(video) {
      const found = await detector.detect(video)
      const codes = found.flatMap((b) => {
        const code = normalizeBarcode(b.rawValue)
        return code ? [code] : []
      })
      return [...new Set(codes)]
    },
  }
}

const OffProductSchema = z.object({
  product: z
    .object({
      product_name_cs: z.string().optional(),
      product_name: z.string().optional(),
      product_name_pl: z.string().optional(),
      product_name_de: z.string().optional(),
      brands: z.string().optional(),
      quantity: z.string().optional(),
    })
    .optional(),
})

/**
 * Nazev vyrobku z Open Food Facts (otevrena databaze, zdarma a bez klice) —
 * jen napoveda u neznameho kodu. Bez signalu nebo bez zaznamu vrati null.
 */
export async function productName(code: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const fields = 'product_name_cs,product_name,product_name_pl,product_name_de,brands,quantity'
    const res = await fetch(`https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(code)}.json?fields=${fields}`, {
      signal: signal ?? AbortSignal.timeout(5000),
    })
    if (!res.ok) return null
    const product = OffProductSchema.safeParse(await res.json()).data?.product
    if (!product) return null
    const clean = (value: string | undefined) => value?.trim() || null
    const name =
      clean(product.product_name_cs) ??
      clean(product.product_name) ??
      clean(product.product_name_pl) ??
      clean(product.product_name_de)
    if (!name) return null
    const brand = clean(product.brands?.split(',')[0])
    const withBrand = brand && !name.toLowerCase().includes(brand.toLowerCase()) ? `${brand} ${name}` : name
    return [withBrand, clean(product.quantity)].filter(Boolean).join(' ').slice(0, 200)
  } catch {
    return null
  }
}
